import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    forwardRef,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Import Quill
import Quill from 'quill';

import { LucideAngularModule, CircleAlert } from 'lucide-angular';
import { HtmlUtils } from '@app/helpers/utils/html.utils';

@Component({
    selector: 'app-quill-editor',
    standalone: true,
    imports: [LucideAngularModule],
    templateUrl: './quill-editor.component.html',
    styleUrls: ['./quill-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuillEditorComponent),
            multi: true,
        },
    ],
})
export class QuillEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @ViewChild('quillEditor', { static: true }) quillEditorRef!: ElementRef;

    // Configuration inputs
    @Input() placeholder: string = 'Start typing...';
    @Input() readonly: boolean = false;
    @Input() theme: 'snow' | 'bubble' = 'snow';
    @Input() formats: string[] = [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'blockquote',
        'code-block',
        'list',
        'indent',
        'link',
        'image',
    ];
    @Input() toolbarConfig: any[] | null = null; // Custom toolbar configuration
    @Input() modules: any = {};
    @Input() bounds: string | HTMLElement = 'body';
    @Input() debug: 'error' | 'warn' | 'log' | boolean = false;
    @Input() scrollingContainer: string | HTMLElement | null = null;

    // Styling inputs
    @Input() containerClass: string = '';
    @Input() editorClass: string = '';
    @Input() showError: boolean = false;
    @Input() hideToolbar: boolean = false;
    /** Read-only viewer: sanitize HTML, then let Quill render its own blocks. */
    @Input() safeDisplay: boolean = false;
    @Input() emphasizeLinks: boolean = false;

    // State inputs
    @Input() disabled: boolean = false;
    @Input() minLength: number = 0;
    @Input() maxLength: number = 0;

    // Events
    @Output() textChange = new EventEmitter<{ html: string; text: string; delta: any }>();
    @Output() selectionChange = new EventEmitter<any>();
    @Output() editorCreated = new EventEmitter<Quill>();
    @Output() blur = new EventEmitter<Quill>();
    @Output() focus = new EventEmitter<Quill>();

    // Icons
    readonly CircleAlert = CircleAlert;

    private htmlUtils = inject(HtmlUtils);
    private quill!: Quill;
    private value: string = '';

    // ControlValueAccessor implementation
    private onChange = (value: string) => { };
    private onTouched = () => { };

    ngOnInit() {
        this.initializeQuill();
    }

    ngOnDestroy() {
        if (this.quill) {
            this.quill.off('text-change');
            this.quill.off('selection-change');
        }
    }

    private initializeQuill() {
        if (this.safeDisplay) {
            this.readonly = true;
            this.disabled = true;
            this.hideToolbar = true;
            this.placeholder = '';
        }

        // Default toolbar configuration
        const defaultToolbarOptions = [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link', 'image'],
            ['clean'],
        ];

        // Use custom toolbar if provided, otherwise use default
        const toolbarOptions = this.toolbarConfig !== null ? this.toolbarConfig : defaultToolbarOptions;

        // Check if image is allowed in toolbar
        const isImageInToolbar = this.isImageInToolbar(toolbarOptions);

        const quillConfig = {
            theme: this.theme,
            placeholder: this.readonly || this.disabled ? '' : this.placeholder,
            readOnly: this.readonly || this.disabled,
            formats: this.formats,
            bounds: this.bounds,
            debug: this.debug,
            scrollingContainer: this.scrollingContainer,
            modules: {
                toolbar: this.hideToolbar ? false : toolbarOptions,
                ...this.modules,
            },
        };

        this.quill = new Quill(this.quillEditorRef.nativeElement, quillConfig);

        if (this.safeDisplay) {
            this.quill.root.setAttribute('contenteditable', 'false');
            this.quill.root.setAttribute('tabindex', '-1');
            this.quill.root.setAttribute('role', 'document');
        }

        // Prevent image paste if image is not in toolbar
        if (!isImageInToolbar) {
            // Block image insertion via clipboard
            this.quill.root.addEventListener('paste', (e: ClipboardEvent) => {
                const clipboardData = e.clipboardData;
                if (clipboardData) {
                    const items = clipboardData.items;
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].type.indexOf('image') !== -1) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                    }
                }
            });

            // Also prevent via Quill's clipboard module
            this.quill.clipboard.addMatcher('IMG', () => {
                return new (Quill as any).import('delta')();
            });
        }

        // Set initial value
        if (this.value) {
            this.quill.clipboard.dangerouslyPasteHTML(this.value);
        }

        // Listen for text changes
        this.quill.on('text-change', (delta, oldDelta, source) => {
            const html = this.quill.root.innerHTML;
            const text = this.quill.getText();

            // Check for images if image is not allowed in toolbar
            if (!isImageInToolbar) {
                const contents = this.quill.getContents();
                const hasImage = contents.ops?.some(
                    (op: any) => op.insert && typeof op.insert === 'object' && op.insert.image
                );

                if (hasImage) {
                    // Remove images from content
                    const newContents = contents.ops?.filter(
                        (op: any) => !(op.insert && typeof op.insert === 'object' && op.insert.image)
                    );
                    this.quill.setContents({ ops: newContents } as any, 'silent');
                    return;
                }
            }

            // Update internal value
            this.value = html === '<p><br></p>' ? '' : html;

            // Emit change events
            this.onChange(this.value);
            this.textChange.emit({ html, text, delta });

            // Length validation
            if (this.maxLength > 0 && text.length > this.maxLength) {
                // Optionally handle max length exceeded
                console.warn(`Text exceeds maximum length of ${this.maxLength} characters`);
            }
        });

        // Listen for selection changes
        this.quill.on('selection-change', (range, oldRange, source) => {
            this.selectionChange.emit({ range, oldRange, source });

            if (range === null && oldRange !== null) {
                this.onTouched();
                this.blur.emit(this.quill);
            } else if (range !== null && oldRange === null) {
                this.focus.emit(this.quill);
            }
        });

        // Update disabled state styling
        this.updateDisabledState();

        // Emit editor created event
        this.editorCreated.emit(this.quill);
    }

    private updateDisabledState() {
        if (this.quill) {
            this.quill.enable(!this.disabled);

            const container = this.quillEditorRef.nativeElement.closest('.quill-editor-container');
            if (container) {
                if (this.disabled) {
                    container.classList.add('ql-disabled');
                } else {
                    container.classList.remove('ql-disabled');
                }
            }
        }

        this.lockDisplayEditor();
    }

    private lockDisplayEditor(): void {
        if (!this.safeDisplay || !this.quill) {
            return;
        }

        this.quill.root.setAttribute('contenteditable', 'false');
        this.quill.root.setAttribute('tabindex', '-1');
        this.quill.root.setAttribute('role', 'document');
    }

    // ControlValueAccessor methods
    writeValue(value: string): void {
        if (value) {
            const normalized = value.replace(/\\\//g, '/');
            this.value = this.safeDisplay
                ? this.htmlUtils.sanitizeQuillHtml(normalized)
                : this.decodeHtmlEntities(normalized);
        } else {
            this.value = '';
        }

        if (this.quill) {
            if (this.value) {
                this.quill.clipboard.dangerouslyPasteHTML(this.value);
            } else {
                this.quill.setText('');
            }
            this.lockDisplayEditor();
        }
    }

    private decodeHtmlEntities(encodedString: string): string {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = encodedString;
        return textArea.value;
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.updateDisabledState();
    }

    // Public methods for external control
    getQuill(): Quill | null {
        return this.quill;
    }

    getContent(): string {
        return this.quill ? this.quill.root.innerHTML : '';
    }

    getText(): string {
        return this.quill ? this.quill.getText() : '';
    }

    getLength(): number {
        return this.quill ? this.quill.getLength() : 0;
    }

    focusEditor(): void {
        if (this.quill) {
            this.quill.focus();
        }
    }

    blurEditor(): void {
        if (this.quill) {
            this.quill.blur();
        }
    }

    setContents(delta: any): void {
        if (this.quill) {
            this.quill.setContents(delta);
        }
    }

    insertText(index: number, text: string, formats?: any): void {
        if (this.quill) {
            this.quill.insertText(index, text, formats);
        }
    }

    insertEmbed(index: number, type: string, value: any): void {
        if (this.quill) {
            this.quill.insertEmbed(index, type, value);
        }
    }

    private isImageInToolbar(toolbarOptions: any[]): boolean {
        // Check if 'image' exists in any toolbar group
        return toolbarOptions.some((group) => {
            if (Array.isArray(group)) {
                return group.includes('image');
            }
            return false;
        });
    }
}
